import { useState } from "preact/hooks";
import type { JSX } from "preact";
import type { StepProps } from "../app";
import type { Attachment } from "../state";
import { createUploadUrl, uploadFileToGcs } from "../lib/api";
import { track } from "../lib/analytics";
import { ContactExpert } from "../ContactExpert";

const MAX_BYTES = 50 * 1024 * 1024;
const BILL_ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/heic"];
const PHOTO_ALLOWED = ["image/jpeg", "image/png", "image/heic"];

const DocIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const PanelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M9 7h6M9 11h6M9 15h3" />
  </svg>
);

/** Both uploads share one card layout so the copy and UX stay parallel. */
interface UploadField {
  key: "bill" | "panelPhoto";
  kind: "bill" | "panel_photo";
  title: string;
  desc: string;
  accept: string;
  capture?: "environment";
  allowed: string[];
  typeError: string;
  icon: JSX.Element;
}

const FIELDS: UploadField[] = [
  {
    key: "bill",
    kind: "bill",
    title: "Recent utility bill",
    desc: "A PDF or photo of a recent bill. Confirms your energy usage.",
    accept: ".pdf,image/*",
    allowed: BILL_ALLOWED,
    typeError: "Please upload a PDF or image.",
    icon: <DocIcon />,
  },
  {
    key: "panelPhoto",
    kind: "panel_photo",
    title: "Electrical panel photo",
    desc: "A photo of your main breaker box. Confirms your service size.",
    accept: "image/*",
    capture: "environment",
    allowed: PHOTO_ALLOWED,
    typeError: "Please upload a photo (JPG, PNG, or HEIC).",
    icon: <PanelIcon />,
  },
];

export function Step3Bill({ state, update, next, back, stepName }: StepProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickFile = (field: UploadField) => (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0] ?? null;
    setError(null);
    if (!file) return;
    if (!field.allowed.includes(file.type)) return setError(field.typeError);
    if (file.size > MAX_BYTES) return setError("File is larger than 50MB.");
    update({ [field.key]: { ...state[field.key], file, mimeType: file.type, sizeBytes: file.size } });
  };

  const onContinue = async () => {
    // Every attachment is optional — the funnel never blocks on them.
    if (!FIELDS.some((f) => state[f.key].file)) return next();
    setBusy(true);
    setError(null);
    try {
      for (const f of FIELDS) {
        const att = state[f.key];
        if (!att.file) continue;
        const { uploadUrl, gcsUri, objectPath } = await createUploadUrl({
          submissionId: state.submissionId,
          file: att.file,
          kind: f.kind,
        });
        await uploadFileToGcs(uploadUrl, att.file);
        update({ [f.key]: { ...att, gcsUri, objectPath } });
        track("file_uploaded", {
          submission_id: state.submissionId,
          file_kind: f.kind,
          size_bytes: att.file.size,
        });
      }
      next();
    } catch (err) {
      setError("Upload failed. You can continue without it and we'll size from your answers.");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const hasAny = FIELDS.some((f) => state[f.key].file);

  return (
    <section class="step">
      <h2>Add your bill and panel photo</h2>
      <p class="hint">
        Both are optional. Add what you have and we'll confirm your usage and service so we can
        often skip the site visit. Up to 50MB each.
      </p>

      <div class="uploads">
        {FIELDS.map((f) => {
          const att: Attachment = state[f.key];
          return (
            <label key={f.key} class={`uploader${att.file ? " filled" : ""}`}>
              <input
                type="file"
                accept={f.accept}
                capture={f.capture}
                onChange={pickFile(f)}
              />
              <span class="u-icon" aria-hidden="true">{att.file ? "✓" : f.icon}</span>
              <span class="u-body">
                <span class="u-title">
                  {f.title}
                  <span class="u-optional">Optional</span>
                </span>
                {att.file ? (
                  <>
                    <span class="u-file">{att.file.name}</span>
                    <span class="u-action">Tap to replace</span>
                  </>
                ) : (
                  <span class="u-desc">{f.desc}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {error && <p class="error" role="alert">{error}</p>}
      <div class="actions">
        <button onClick={back} disabled={busy}>Back</button>
        <ContactExpert stepName={stepName} submissionId={state.submissionId} />
        <button class="primary" onClick={onContinue} disabled={busy}>
          {busy ? "Uploading…" : hasAny ? "Continue" : "Skip for now"}
        </button>
      </div>
    </section>
  );
}
